import { useEffect, useRef, useState } from "react";
import { Instagram, ExternalLink } from "lucide-react";

let _scriptLoaded = false;
let _scriptLoading = false;

function loadInstagramScript(): Promise<void> {
  if (_scriptLoaded) return Promise.resolve();
  if (_scriptLoading) {
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (_scriptLoaded) {
          clearInterval(check);
          resolve();
        }
      }, 100);
    });
  }
  _scriptLoading = true;
  return new Promise((resolve) => {
    const existing = document.querySelector('script[src*="instagram.com/embed.js"]');
    if (existing) {
      _scriptLoaded = true;
      _scriptLoading = false;
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://www.instagram.com/embed.js";
    script.async = true;
    script.onload = () => {
      _scriptLoaded = true;
      _scriptLoading = false;
      resolve();
    };
    script.onerror = () => {
      _scriptLoading = false;
      resolve();
    };
    document.body.appendChild(script);
  });
}

declare global {
  interface Window {
    instgrm?: { Embeds: { process: (el?: HTMLElement) => void } };
  }
}

export interface InstagramEmbedProps {
  /**
   * Instagram handle to embed. Accepts with or without the "@" prefix.
   * Example: "studiomindmagik" or "@studiomindmagik"
   */
  username: string;
}

/**
 * InstagramEmbed
 *
 * Lazy-loads the Instagram embed.js script (once per page) and renders
 * the official Instagram profile embed for a given handle.
 *
 * Includes a header with avatar/gradient ring, username, and a Follow button,
 * plus a footer "View Full Profile" link — all styled for the dark aesthetic.
 *
 * @example
 * <InstagramEmbed username="@studiomindmagik" />
 */
export function InstagramEmbed({ username }: InstagramEmbedProps) {
  const handle = username.replace("@", "");
  const profileUrl = `https://www.instagram.com/${handle}/`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [key, setKey] = useState(0);

  useEffect(() => {
    setKey((k) => k + 1);
  }, [handle]);

  useEffect(() => {
    let cancelled = false;
    loadInstagramScript().then(() => {
      if (!cancelled && containerRef.current && window.instgrm) {
        window.instgrm.Embeds.process(containerRef.current);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [handle, key]);

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 rounded-full p-[2px]">
            <div className="w-full h-full bg-black rounded-full flex items-center justify-center">
              <Instagram size={18} className="text-white" />
            </div>
          </div>
          <div>
            <h3 className="font-bold text-sm text-white">{username}</h3>
            <p className="text-xs text-muted-foreground">Instagram</p>
          </div>
        </div>
        <a
          href={profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs bg-accent/20 border border-accent/30 hover:bg-accent hover:text-white text-white px-4 py-2 rounded-full font-bold uppercase tracking-widest transition-colors"
        >
          Follow
        </a>
      </div>

      <div
        ref={containerRef}
        key={key}
        className="instagram-embed-container [&_iframe]:!min-w-0 [&_iframe]:!w-full"
      >
        <blockquote
          className="instagram-media"
          data-instgrm-permalink={profileUrl}
          data-instgrm-version="14"
          style={{
            background: "transparent",
            border: 0,
            margin: 0,
            padding: 0,
            maxWidth: "100%",
            width: "100%",
          }}
        />
      </div>

      <a
        href={profileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 text-accent text-xs uppercase tracking-widest font-bold hover:gap-3 transition-all py-3"
      >
        View Full Profile <ExternalLink size={12} />
      </a>
    </div>
  );
}
